import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { duplicateTrackingId } from '@returnmates/client-core/src/constants/errors'
import {
  Media,
  Package,
  PackageCondition,
  PackageStatus,
  PreviousAddressSource,
  Scan,
  ScanType,
  Trip,
  TripStatus,
  TripType,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getAddressValidationFeedback,
  getCarriers as getCarriersSelector,
  getRetailers as getRetailersSelector,
  packageConsolidationSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { getCurrentPackage } from '@returnmates/client-core/src/selectors/packages'
import { getCurrentUser as getCurrentUserSelector } from '@returnmates/client-core/src/selectors/user'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { checkIsAddressChanged } from '@returnmates/client-core/src/utils/checkIsAddressChanged'
import cognitoErrorMapper from '@returnmates/client-core/src/utils/errorMapper'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import validate from '@returnmates/client-core/src/utils/validation/package'
import Button from '@returnmates/ui-core/src/components/Button'
import SwaySpinner from '@returnmates/ui-core/src/components/SwaySpinner'
import clsx from 'clsx'
import { FORM_ERROR, setIn } from 'final-form'
import arrayMutators from 'final-form-arrays'
import moment from 'moment'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FlyoutSidebarContext } from '../../pages/Main'
import EditPackageFormInner from './components/EditPackageFormInner'
import MissingLabelModal from './components/MissingLabelModal'
import MultiPackageModal from './components/MultiPackageModal'
import { DIGITAL_LABELS } from './constants'
import useStyles from './styles'
import { Modals } from './types'

interface Props {
  id: string
}

function EditPackageForm({ id }: Props) {
  const classes = useStyles({})
  const dispatch = useDispatch()
  const carriers = useSelector(getCarriersSelector)
  const retailers = useSelector(getRetailersSelector)
  const currentUser = useSelector(getCurrentUserSelector)
  const currentPackage: Package | null = useSelector(getCurrentPackage)
  const packageConsolidation = useSelector(packageConsolidationSelector)
  const addressValidationFeedback = useSelector(getAddressValidationFeedback)
  const { handleCloseSidebar } = useContext(FlyoutSidebarContext)
  const [isPackageValid, setIsPackageValid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<{ [key: string]: string } | null>(null)
  const [modalTypes, setModalsTypes] = useState<Modals[]>([])
  const [isPackageUpdateLoading, setIsPackageUpdateLoading] = useState(false)
  const [isCurrentPackageProblem, setIsCurrentPackageProblem] = useState<boolean>(
    currentPackage?.status === PackageStatus.PROBLEM,
  )

  const isAddressInvalid = useMemo(() => {
    return (
      addressValidationFeedback?.isValid === false &&
      currentPackage?.status === PackageStatus.PROBLEM
    )
  }, [addressValidationFeedback?.isValid, currentPackage?.status])

  const isAddressPlassible = useMemo(() => {
    return (
      addressValidationFeedback?.isPlausible &&
      addressValidationFeedback?.isValid &&
      (isCurrentPackageProblem || currentPackage?.status === PackageStatus.PROBLEM)
    )
  }, [
    addressValidationFeedback?.isPlausible,
    addressValidationFeedback?.isValid,
    isCurrentPackageProblem,
    currentPackage?.status,
  ])

  const isSaveButtonDisabled = useMemo(() => isAddressInvalid && isCurrentPackageProblem, [
    isAddressInvalid,
    isCurrentPackageProblem,
  ])

  const checkLabelModal = useCallback(
    (pack: Package) => {
      if (
        DIGITAL_LABELS.includes(pack.labelType) &&
        !pack.labelUrl &&
        !modalTypes.includes(Modals.LABEL)
      ) {
        setModalsTypes(prev => [...prev, Modals.LABEL])

        return true
      }
      return false
    },
    [modalTypes],
  )

  const fetchTripInfo = useCallback(
    async (pack: Package) => {
      try {
        const trip: Trip = await createAsyncAction(
          dispatch,
          actions.getTrip.request({ id: pack?.tripId }),
        )

        setIsPackageValid(!!trip.address?.isValid)

        return trip
      } catch (error) {
        setIsPackageUpdateLoading(false)

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(error as string),
          }),
        )

        throw error
      }
    },
    [dispatch],
  )

  const updatePackageInfo = useCallback(
    async (pack: Package, isMultiPackage: boolean) => {
      if (!isMultiPackage) {
        await createAsyncAction(
          dispatch,
          actions.updatePackage.request({
            id: pack.id,
            input: {
              addressId: pack.address?.id,
              labelType: pack.labelType,
              status: pack.status,
              labelUrl: pack.labelUrl ?? 'https://returnmates.com/null',
              description: pack.description,
              carrierId: pack.carrier?.id,
              retailerId: pack.retailer?.id || null,
              partnerCode: pack.partner?.code,
              trackingId: pack.trackingId,
              condition: pack.condition,
              tripId: pack?.tripId || null,
              requiresBox: pack.requiresBox,
              isSwap: pack.isSwap,
              returnDestination: pack.returnDestination,
              returnDestinationId: pack.returnDestinationId || null,
              serviceType: pack.serviceType,
              date: moment(pack.packageDate).format('yyyy-MM-DD'),
              length: Number(pack.length),
              width: Number(pack.width),
              height: Number(pack.height),
              weight: Number(pack.weight),
              packageDelayReason: pack.packageDelayReason,
              packageProblemReason: pack.packageProblemReason,
              requiresAgeVerification: pack.requiresAgeVerification,
              requiresDeliverySignature: pack.requiresDeliverySignature,
              claim: pack.claim,
              claimReason: pack.claimReason,
              method: pack.method,
            },
          }),
        )
      }
    },
    [dispatch],
  )

  const updateAddressInfo = useCallback(
    async (pack: Package) => {
      const currentAddress = currentUser?.addresses.find(
        userAddress => userAddress?.id === currentPackage?.address?.id,
      )
      const updatedAddress = pack?.address
        ? { ...pack.address, isValid: addressValidationFeedback?.isValid }
        : null
      if (
        checkIsAddressChanged({
          updatedAddress,
          currentAddress,
        })
      ) {
        try {
          await createAsyncAction(
            dispatch,
            actions.updateAddress.request({
              id: pack.address?.id,
              input: {
                name: pack.address?.name,
                phone: pack.address?.phone,
                street: pack.address?.street,
                city: pack.address?.city,
                state: pack.address?.state,
                zipCode: pack.address?.zipCode,
                unit: pack.address?.unit,
                instructions: pack.address?.instructions,
                isValid: addressValidationFeedback?.isValid ?? isPackageValid,
              },
            }),
          )

          if (currentPackage?.tripId) {
            await createAsyncAction(
              dispatch,
              actions.updateTripPackagesAddressId.request({
                id: currentPackage?.tripId,
                input: {
                  addressId: pack.address?.id,
                },
              }),
            )
          }

          if (currentAddress) {
            await createAsyncAction(
              dispatch,
              actions.upsertPreviousAddressPackage.request({
                packageId: pack.id,
                input: {
                  addressId: currentAddress.id,
                  name: currentAddress.name,
                  phone: currentAddress.phone,
                  street: currentAddress.street,
                  city: currentAddress.city,
                  state: currentAddress.state,
                  zipCode: currentAddress.zipCode,
                  unit: currentAddress.unit,
                  instructions: currentAddress.instructions,
                  source: PreviousAddressSource.WILSON,
                  userId: currentUser?.id,
                },
              }),
            )
          }
        } catch (err) {
          const { message } = err as Error

          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: errorMapper(message || (err as string)),
            }),
          )
        }
      }
    },
    [
      addressValidationFeedback?.isValid,
      currentPackage?.address?.id,
      currentPackage?.tripId,
      currentUser?.addresses,
      currentUser?.id,
      dispatch,
      isPackageValid,
    ],
  )

  const updateConsolidationInfo = useCallback(
    async (pack: Package & { consolidationId: string }) => {
      const hasConsolidationIdChanged =
        packageConsolidation?.consolidationId !== pack.consolidationId

      const needRemovePackageFromConsolidation =
        packageConsolidation?.consolidationId &&
        (hasConsolidationIdChanged ||
          [PackageStatus.FAILED, PackageStatus.CANCELED].includes(pack.status))

      if (needRemovePackageFromConsolidation) {
        await createAsyncAction(
          dispatch,
          actions.deleteConsolidationPackage.request({
            packageId: pack.id,
            consolidationId: packageConsolidation?.consolidationId,
          }),
        )
      }

      if (pack.consolidationId && hasConsolidationIdChanged) {
        await createAsyncAction(
          dispatch,
          actions.createConsolidationPackage.request({
            packageId: pack.id,
            consolidationId: pack.consolidationId,
          }),
        )
      }
    },
    [dispatch, packageConsolidation?.consolidationId],
  )

  const updateMedia = useCallback(
    async (pack: Package) => {
      const mediaToDelete =
        currentPackage?.media?.filter(
          mediaItem => !pack.media?.find((media: Media) => media.id === mediaItem?.id),
        ) || []

      for (let i = 0; i < mediaToDelete.length; i++) {
        if (mediaToDelete[i]?.id) {
          await createAsyncAction(
            dispatch,
            actions.deleteTripMedia.request({ id: mediaToDelete[i]?.id }),
          )
        }
      }

      const mediaToCreate = pack.media?.filter(mediaItem => !mediaItem?.id) || []

      for (let i = 0; i < mediaToCreate.length; i++) {
        await createAsyncAction(
          dispatch,
          actions.createTripMedia.request({
            tripId: pack.tripId,
            url: mediaToCreate[i]?.url,
            packageId: pack.id,
            type: 'image/png',
          }),
        )
      }
    },
    [currentPackage?.media, dispatch],
  )

  const handleErrors = useCallback(
    (err: string) => {
      let errors = {}
      const submitError = { [FORM_ERROR]: cognitoErrorMapper(err) }

      const setError = (key: string, value: string) => {
        errors = setIn(errors, key, value)
      }

      if (submitError[FORM_ERROR] === duplicateTrackingId) {
        setError('trackingId', duplicateTrackingId)
      }

      setSubmitError(errors)

      if (Object.entries(errors).length > 0) {
        return errors
      } else {
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: submitError[FORM_ERROR],
          }),
        )
      }
    },
    [dispatch],
  )

  const checkAddressIdChanged = useCallback(
    (pack: Package) => currentPackage?.address?.id !== pack.address?.id,
    [currentPackage?.address?.id],
  )

  const updatePackage = useCallback(
    async (pack: Package & { consolidationId: string }) => {
      try {
        if (checkLabelModal(pack)) return

        setIsPackageUpdateLoading(true)

        let isMultiPack = false

        if (
          checkAddressIdChanged(pack) &&
          !modalTypes.includes(Modals.MULTI_PACKAGE) &&
          pack?.tripId
        ) {
          const trip = await fetchTripInfo(pack)
          isMultiPack = trip?.packages?.length > 1
        }

        setModalsTypes([])

        // If package is in PROBLEM status and address is validated, change status to PENDING
        const shouldChangeToPending =
          (currentPackage?.status === PackageStatus.PROBLEM ||
            pack.status === PackageStatus.PROBLEM) &&
          addressValidationFeedback?.isValid

        const updatedPack = shouldChangeToPending
          ? { ...pack, status: PackageStatus.PENDING, packageProblemReason: null }
          : pack

        await updateAddressInfo(updatedPack)
        await updatePackageInfo(updatedPack, isMultiPack)
        await updateConsolidationInfo(updatedPack)
        await updateMedia(updatedPack)

        return true
      } catch (err) {
        const { message } = err as { message: string }

        handleErrors(message || (err as string))
      } finally {
        setIsPackageUpdateLoading(false)
      }
    },
    [
      checkLabelModal,
      modalTypes,
      updatePackageInfo,
      updateAddressInfo,
      updateConsolidationInfo,
      updateMedia,
      fetchTripInfo,
      handleErrors,
      checkAddressIdChanged,
      currentPackage?.status,
      addressValidationFeedback?.isValid,
    ],
  )

  const customValidation = useCallback(
    values => {
      const validation = validate(values)

      return { ...validation, ...submitError }
    },
    [submitError],
  )

  const handleMissingLabelModalClose = useCallback(
    () => setModalsTypes(prev => prev.filter(items => items !== Modals.LABEL)),
    [],
  )

  const handleMultiPackageModalClose = useCallback(
    () => setModalsTypes(prev => prev.filter(items => items !== Modals.MULTI_PACKAGE)),
    [],
  )

  const getPackageConsolidation = useCallback(async () => {
    try {
      if (id && id !== currentPackage?.id) {
        await createAsyncAction(
          dispatch,
          actions.getPackageConsolidation.request({
            packageId: id,
          }),
        )
      }
    } catch (error) {
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: error,
        }),
      )
    }
  }, [currentPackage?.id, dispatch, id])

  const getPackage = useCallback(async () => {
    try {
      if (id && id !== currentPackage?.id) {
        setIsLoading(true)
        await createAsyncAction(
          dispatch,
          actions.getPackage.request({
            id,
          }),
        )
      }
    } catch (error) {
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: error,
        }),
      )
    } finally {
      setIsLoading(false)
    }
  }, [id, currentPackage?.id, setIsLoading, dispatch])

  const canSaveAndReceive = useMemo(() => {
    const { status, partner, trackingId, carrier, condition, scans, tripType, tripStatus } =
      currentPackage || {}

    return (
      status === PackageStatus.PICKED_UP &&
      partner &&
      (!partner.requiresReceiptTracking || (trackingId && carrier)) &&
      condition !== PackageCondition.PENDING &&
      !scans?.some((scan: Scan) => scan.type === ScanType.INGEST) &&
      tripType === TripType.PICKUP &&
      tripStatus === TripStatus.COMPLETE
    )
  }, [currentPackage])

  useEffect(() => {
    getPackageConsolidation()
  }, [getPackageConsolidation])

  useEffect(() => {
    getPackage()
  }, [getPackage])

  useEffect(() => {
    if (!carriers) {
      dispatch(actions.getCarriers.request())
    }

    if (!retailers) {
      dispatch(actions.getRetailers.request())
    }
  }, [carriers, dispatch, retailers])

  useEffect(() => {
    if (id !== currentPackage?.id) {
      dispatch(actions.clearPackageTrackingList.request())
    }
  }, [currentPackage?.id, dispatch, id])

  useEffect(() => {
    return () => {
      dispatch(actions.clearCurrentPackage.request())
    }
  }, [dispatch])

  if (isLoading) {
    return <SwaySpinner classes={{ spinnerWrapper: classes.spinner }} />
  }

  return (
    <Form
      initialValues={
        {
          ...currentPackage,
          consolidationId: packageConsolidation?.consolidationId,
        } as Package & { consolidationId: string }
      }
      onSubmit={updatePackage}
      validate={customValidation}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, values }) => (
        <>
          {currentPackage ? (
            <EditPackageFormInner setIsCurrentPackageProblem={setIsCurrentPackageProblem} />
          ) : null}
          <div className={classes.buttonsContainer}>
            <Button
              className={clsx(classes.button, classes.cancelButton)}
              onClick={handleCloseSidebar}
              label="Cancel"
              cancelButton
            />
            <Button
              className={clsx(classes.button, classes.saveButton)}
              onClick={handleSubmit}
              label={isAddressPlassible ? 'Accept Plausible' : 'Save'}
              isLoading={isPackageUpdateLoading}
              disabled={isSaveButtonDisabled}
            />
            {canSaveAndReceive ? (
              <Button
                className={clsx(classes.button, classes.saveButton, classes.receiveButton)}
                onClick={() =>
                  updatePackage({
                    ...values,
                    status: PackageStatus.RECEIVED,
                  })
                }
                label="Save &amp; Receive"
                isLoading={isPackageUpdateLoading}
              />
            ) : null}
          </div>
          <MissingLabelModal
            isOpen={modalTypes.includes(Modals.LABEL)}
            onClose={handleMissingLabelModalClose}
            handleSubmit={handleSubmit}
          />
          <MultiPackageModal
            isOpen={modalTypes.includes(Modals.MULTI_PACKAGE)}
            onClose={handleMultiPackageModalClose}
            onSubmit={handleSubmit}
            addressId={values.address?.id}
            setIsPackageUpdateLoading={setIsPackageUpdateLoading}
          />
        </>
      )}
    />
  )
}

export default memo(EditPackageForm)
